import { Command as CommandPrimitive } from "cmdk";
import {
  useCallback,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import React from "react";
import { InputGroup } from "@volca/ui/components/ui/input-group";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import {
  AUTOCOMPLETE_INPUT_CLASS,
  AutoCompleteField,
  requiredPlaceholder,
  useCmdkInput,
} from "./autocomplete-field";
import { AutoCompleteList } from "./autocomplete-list";
import { focusNextTabbable } from "./focus-next-tabbable";

type Props<T extends string> = {
  id?: string;
  ariaLabel?: string;
  selectedValue: T;
  onSelectedValueChange: (value: T) => void;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  items: { value: T; label: string }[];
  renderOption?: (option: { value: T; label: string }) => React.ReactNode;
  isLoading?: boolean;
  emptyMessage?: string;
  placeholder?: string;
  label?: string;
  error?: string;
  showErrorMessage?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  disableRequirementAsterisk?: boolean;
  className?: string;
  onAddNew?: () => void;
  addNewLabel?: string;
  scrollAreaClassName?: string;
  containScroll?: boolean;
  disabled?: boolean;
  disabledTooltip?: React.ReactNode | string;
};

export const AutoComplete = React.forwardRef<HTMLInputElement, Props<string>>(
  (
    {
      id,
      ariaLabel,
      selectedValue,
      onSelectedValueChange,
      searchValue,
      onSearchValueChange,
      items,
      renderOption,
      isLoading,
      emptyMessage = "No items.",
      placeholder = "Search…",
      label,
      error,
      showErrorMessage = true,
      fullWidth,
      required,
      disableRequirementAsterisk,
      className,
      onAddNew,
      scrollAreaClassName,
      containScroll,
      addNewLabel,
      disabled,
      disabledTooltip,
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    const [isOpen, setOpen] = useState(false);
    const [hasFocused, setHasFocused] = useState(false);
    const [highlightedValue, setHighlightedValue] = useState("");
    const [isClickingAddNew, setIsClickingAddNew] = useState(false);
    const isSelectedValueInactive = Boolean(selectedValue && !hasFocused);
    const labelId = useId();
    const errorId = useId();
    const inputId = useCmdkInput(inputRef, label ? labelId : undefined);

    React.useImperativeHandle(ref, () => inputRef.current!, []);

    React.useEffect(() => {
      const selectedItem = items.find((item) => item.value === selectedValue);
      if (selectedItem && !searchValue && !hasFocused) {
        onSearchValueChange(selectedItem.label);
      }
    }, [selectedValue, items, searchValue, onSearchValueChange, hasFocused]);

    React.useLayoutEffect(() => {
      if (isOpen && listRef.current) listRef.current.scrollTop = 0;
    }, [isOpen, searchValue]);

    React.useLayoutEffect(() => {
      const list = listRef.current;
      if (!isOpen || !list) return;

      const firstItem = list.querySelector("[cmdk-item]");
      if (firstItem?.getAttribute("data-value") === highlightedValue) {
        list.scrollTop = 0;
      }
    }, [isOpen, highlightedValue, searchValue]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (!input) return;

        if (event.key === "Tab") return;

        if (!isOpen) {
          setOpen(true);
        }

        if (event.key === "Enter" && input.value !== "") {
          const selectedItem =
            items.find((item) => item.value === highlightedValue) ??
            items.find((item) => item.label === input.value);
          if (selectedItem) {
            onSelectedValueChange(selectedItem.value);
            onSearchValueChange(selectedItem.label);
            setOpen(false);
            setHasFocused(false);
          }
        }

        if (event.key === "Escape") {
          input.blur();
        }
      },
      [
        highlightedValue,
        isOpen,
        items,
        onSelectedValueChange,
        onSearchValueChange,
      ],
    );

    const handleInputKeyDownCapture = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Tab") {
          event.preventDefault();
          event.stopPropagation();
          focusNextTabbable(event.currentTarget, event.shiftKey);
        }
      },
      [],
    );

    const handleFocus = useCallback(() => {
      if (disabled) return;
      setOpen(true);
      setHasFocused(true);
      onSearchValueChange("");
    }, [onSearchValueChange, disabled]);

    const handleInputClick = useCallback(() => {
      if (disabled || hasFocused) return;
      setOpen(true);
      setHasFocused(true);
      onSearchValueChange("");
    }, [disabled, hasFocused, onSearchValueChange]);

    const handleBlur = useCallback(() => {
      if (isClickingAddNew) {
        setIsClickingAddNew(false);
        return;
      }

      setOpen(false);
      setHasFocused(false);
      const selectedItem = items.find((item) => item.value === selectedValue);
      if (selectedItem) {
        onSearchValueChange(selectedItem.label);
      } else {
        onSearchValueChange("");
      }
    }, [selectedValue, items, onSearchValueChange, isClickingAddNew]);

    const handleSelectOption = useCallback(
      (value: string) => {
        const selectedItem = items.find((item) => item.value === value);
        if (selectedItem?.value === selectedValue) {
          onSelectedValueChange("" as string);
          onSearchValueChange("");
          setOpen(false);
          setHasFocused(false);
        } else if (selectedItem) {
          onSelectedValueChange(selectedItem.value);
          onSearchValueChange(selectedItem.label);
          setOpen(false);
          setHasFocused(false);
        }
      },
      [items, onSelectedValueChange, onSearchValueChange, selectedValue],
    );

    const hasErrorMessage = Boolean(error && showErrorMessage);

    const field = (
      <AutoCompleteField
        label={label}
        htmlFor={inputId}
        labelId={labelId}
        required={required}
        disableRequirementAsterisk={disableRequirementAsterisk}
        errorId={errorId}
        error={error}
        showErrorMessage={showErrorMessage}
        disabled={disabled}
        fullWidth={fullWidth}
      >
        <CommandPrimitive
          onKeyDown={handleKeyDown}
          value={highlightedValue}
          onValueChange={setHighlightedValue}
          onMouseLeave={() => setHighlightedValue("")}
        >
          <InputGroup className={className}>
            <CommandPrimitive.Input
              id={id}
              data-slot="input-group-control"
              aria-label={ariaLabel}
              ref={inputRef}
              value={searchValue}
              onValueChange={onSearchValueChange}
              onBlur={handleBlur}
              autoFocus={false}
              onFocus={handleFocus}
              onClick={handleInputClick}
              onKeyDownCapture={handleInputKeyDownCapture}
              placeholder={requiredPlaceholder(placeholder, {
                label,
                required,
                disableRequirementAsterisk,
              })}
              className={cn(AUTOCOMPLETE_INPUT_CLASS, {
                "caret-transparent": isSelectedValueInactive,
              })}
              aria-invalid={error ? true : undefined}
              aria-required={required || undefined}
              aria-describedby={hasErrorMessage ? errorId : undefined}
              disabled={disabled}
            />
          </InputGroup>
          <AutoCompleteList
            listRef={listRef}
            open={isOpen && !disabled}
            items={items}
            selectedValue={selectedValue}
            onSelectOption={handleSelectOption}
            renderOption={renderOption}
            isLoading={isLoading}
            emptyMessage={emptyMessage}
            scrollAreaClassName={scrollAreaClassName}
            containScroll={containScroll}
            onAddNew={
              onAddNew &&
              (() => {
                onAddNew();
                inputRef.current?.blur();
              })
            }
            addNewLabel={addNewLabel || `+ New ${label || "item"}`}
            onAddNewHover={() => setHighlightedValue("")}
            onAddNewPress={() => setIsClickingAddNew(true)}
          />
        </CommandPrimitive>
      </AutoCompleteField>
    );

    if (disabled && disabledTooltip) {
      return (
        <Tooltip>
          <TooltipTrigger
            render={<div className={cn({ "w-full": fullWidth })} />}
          >
            {field}
          </TooltipTrigger>
          <TooltipContent>{disabledTooltip}</TooltipContent>
        </Tooltip>
      );
    }

    return field;
  },
);
